import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext.ts";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon.tsx";
import { logError } from "@entrilia/shared/logging.ts";
import { Alert, Link, Stack, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import React, { memo, useCallback } from "react";
import { FieldConfigurationType } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { intercomArticleIds } from "@entrilia/shared/utilities/intercomArticles";
import type { ConfigurationValidReturnType } from "../../../utilities/isConfigurationValid.ts";
import { InvalidConfigurationReason } from "../../../utilities/isConfigurationValid.ts";

interface ValidationField {
  config: { guid: string; type: FieldConfigurationType; customLabel?: string | null };
  meta: { caption: string };
}

interface Props {
  state: ConfigurationValidReturnType;
  fields: ValidationField[];
}

const ValidationState = ({ state, fields, children }: PropsWithChildren<Props>) => {
  const errorTitle = React.useMemo(() => {
    if (state.validation === "valid" || state.reason === undefined) return <></>;
    const measureField = fields.find((v) => v.config.guid === state.measure);
    if (measureField === undefined) return <></>;
    const caption =
      measureField.config.type === FieldConfigurationType.Aggregation
        ? "Aggregation"
        : measureField.config.type === FieldConfigurationType.Measure
          ? "Measure"
          : "Metric";
    return (
      <Stack direction="row" useFlexGap spacing={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
          {caption}
        </Typography>
        <Typography variant="subtitle1" color="error">
          {measureField.config.customLabel || measureField.meta.caption}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
          has been configured incorrectly
        </Typography>
      </Stack>
    );
  }, [state, fields]);

  const errorText = React.useMemo(() => {
    if (state.validation === "valid" || state.reason === undefined) {
      return "";
    }

    switch (state.reason) {
      case InvalidConfigurationReason.ConditionsFieldAndCustomConditions: {
        return "Scenario with Date Dimension in the Conditions, Fields and in the Measure Custom Conditions is not supported";
      }
      case InvalidConfigurationReason.FieldAndCustomConditions: {
        return "Scenario with Date Dimension in the Fields and in the Measure Custom Conditions is not supported";
      }
      case InvalidConfigurationReason.MultipleCustomConditions: {
        return "Scenario with Multiple Date Dimensions in the Fields is not supported";
      }
      case InvalidConfigurationReason.MultipleFields: {
        return "Scenario with Multiple Date Dimensions in the Measure Custom Conditions is not supported";
      }
      case InvalidConfigurationReason.NoDateFields: {
        return "At least one Date Dimension is required to be added to the Conditions, Fields or Measures' Custom Conditions";
      }
      case InvalidConfigurationReason.MandatoryFieldIsNotSelected: {
        return "For report generation, kindly ensure the mandatory fields in the conditions are filled.";
      }
      case InvalidConfigurationReason.TooManySelectedValuesInConditions: {
        return <TooManySelectedValuesInConditions />;
      }
      default: {
        logError("Unknown InvalidConfigurationReason", state.reason);
        return "Configuration is not valid";
      }
    }
  }, [state]);

  if (state.validation === "warning") {
    return (
      <>
        <Alert severity="warning">{errorText}</Alert>
        {children}
      </>
    );
  }

  if (state.validation === "valid" || state.reason === undefined) {
    return children;
  }

  return (
    <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center", flex: 1, mt: -10 }}>
      <WarningDialogIcon sx={{ width: 80, height: 80 }} />
      <Stack spacing={1} sx={{ alignItems: "center", justifyContent: "center" }}>
        {errorTitle}
        <Typography sx={{ textAlign: "center" }}>{errorText}</Typography>
      </Stack>
    </Stack>
  );
};

export default ValidationState;

const TooManySelectedValuesInConditions = memo(function TooManySelectedValuesInConditions() {
  const { showArticle } = useIntercomContext();
  const handleReadMore = useCallback(() => {
    showArticle(intercomArticleIds.CONDITION_SETUP_GUIDE);
  }, [showArticle]);

  return (
    <Typography>
      One of the conditions includes too many selected values. The filter supports up to 1000 selections. Please review
      the condition and reduce the number of selected items.{" "}
      <Link
        component={"span"}
        sx={{ textDecoration: "underline", cursor: "pointer", color: "inherit" }}
        onClick={handleReadMore}
      >
        Read more.
      </Link>
    </Typography>
  );
});
